/**
 * TASK LIST — Home page. Nuovo design (port dal mockup) sul telaio (page-head +
 * tab a pill + card), MA con DataGrid MUI per la tabella: così restano
 * ordinamento, filtro, nascondi e "gestisci colonne" dal menu di colonna,
 * mentre le celle usano lo stile del mockup (tick, chip, pallino, ecc.).
 *
 * <p>Due viste commutabili dal telaio: tabella e bacheca. La bacheca è la
 * `KanbanBoard` condivisa con la board dei preventivi, in sola lettura — non una
 * griglia di colonne scritta qui: due implementazioni della stessa cosa
 * divergerebbero alla prima modifica. Le colonne SONO gli stati, quindi il
 * filtro per tab non le tocca; restano ricerca e faccette, che passano dallo
 * stesso `applyFacets` della tabella.
 */

import { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useServerSearch, formatDateTime } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchTasks, deleteTask, createTask, updateTaskStatus, FETCH_ALL_PAGE_SIZE } from './taskThunks';
import { setActiveTab, setTaskFilters } from './taskSlice';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { useToast } from '../../components/useToast';
import { ImportButton } from '../../components/ImportButton';
import { AllTasksDoneCelebration } from '../../components/AllTasksDoneCelebration';
import { TaskEditModal } from './TaskEditModal';
import { useAllTasksCompleted } from './useAllTasksCompleted';
import { TASK_TYPES, type TaskTab, type Task, type TaskCreatePayload } from './taskTypes';
import { isTaskCompleted, taskMatchesTab, taskDisplayStatus } from './taskStatus';
import {
  SearchGlyph, CheckGlyph, TrashGlyph, PlusGlyph, QueuesGlyph,
  ImportGlyph, TableGlyph, BoardGlyph,
} from '../../components/icons';
import { KanbanBoard, type KanbanColumn, type KanbanBoardItem } from '../../components/KanbanBoard';
import { TaskKanbanCard } from './TaskKanbanCard';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { userApi } from '../user/userApi';
import { queueApi } from '../queue/queueApi';
import type { Queue } from '../queue/queueTypes';

const TASK_TAB_KEYS: TaskTab[] = ['expiring_today', 'expired', 'imminent', 'all'];

const STATUS_CHIP: Record<string, string> = {
  expired: 'crit', expiring_today: 'warn', imminent: 'info', todo: 'neutral', completed: 'ok',
};
const PRIO_DOT: Record<string, string> = { high: 'high', medium: 'med', low: 'low', none: 'low' };

interface TaskListProps {
  onCreateClick: () => void;
}

export default function TaskList({ onCreateClick }: TaskListProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const toast = useToast();

  const { items, status, error, activeTab, filters } = useAppSelector((state) => state.tasks);
  const { search, setSearch, query } = useServerSearch();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [usersById, setUsersById] = useState<Map<number, string>>(new Map());
  const groupId = useCurrentGroupId();

  const currentSelected = useMemo(
    () => (selectedTask ? items.find((it) => it.taskId === selectedTask.taskId) ?? null : null),
    [items, selectedTask],
  );

  const { isGloballyEmpty, justCompleted } = useAllTasksCompleted(!!query);

  const TABS: { key: TaskTab; label: string }[] = [
    { key: 'expiring_today', label: t('task.tabs.dueToday') },
    { key: 'expired', label: t('task.tabs.overdue') },
    { key: 'imminent', label: t('task.tabs.upcoming') },
    { key: 'all', label: t('task.tabs.all') },
  ];

  const counts = useMemo(
    () => ({
      expiring_today: items.filter((tk) => taskMatchesTab(tk, 'expiring_today')).length,
      expired: items.filter((tk) => taskMatchesTab(tk, 'expired')).length,
      imminent: items.filter((tk) => taskMatchesTab(tk, 'imminent')).length,
      all: items.length,
    }),
    [items],
  );

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && TASK_TAB_KEYS.includes(tab as TaskTab)) dispatch(setActiveTab(tab as TaskTab));
  }, [searchParams, dispatch]);

  useEffect(() => {
    setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 }));
  }, [query, activeTab, filters]);

  useEffect(() => {
    dispatch(fetchTasks({ pageSize: FETCH_ALL_PAGE_SIZE, search: query || undefined }));
  }, [query, dispatch]);

  async function handleToggleCompleted(task: Task) {
    const next = task.status === 'completed' ? 'todo' : 'completed';
    try {
      await dispatch(updateTaskStatus({ taskId: task.taskId, status: next })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleDelete(taskId: number) {
    try {
      await dispatch(deleteTask(taskId)).unwrap();
      dispatch(fetchTasks({ pageSize: FETCH_ALL_PAGE_SIZE, search: query || undefined }));
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  // Filtri tab + faccette client-side sull'intero set caricato. I tab
  // Scade oggi/Scaduti/Imminenti usano lo stato DERIVATO dalle date
  // (taskMatchesTab): il backend persiste solo TODO/COMPLETED, gli altri
  // stati sono calcolati dalla expirationDate.
  //
  // Il predicato dei tab non e' il bucket del chip: "Imminente" e' un
  // sottoinsieme di "In scadenza oggi", e un task che sta in due tab non puo'
  // uscire da una funzione che ne restituisce uno solo.
  //
  // NB: "Imminente" guarda l'ora corrente, ma queste deps non includono il
  // tempo — un task che scade mentre la lista e' aperta resta nel tab fino al
  // primo re-render utile. Accettabile finche' la lista e' una fotografia;
  // se servisse la precisione al minuto ci vorrebbe un ticker.
  // Filtri "faccetta" (assegnatario/tipo/coda) condivisi da tabella e bacheca: un solo
  // punto, così le due viste non divergono (la tabella aggiunge il filtro per tab).
  const applyFacets = useCallback(
    (list: Task[]) =>
      list
        .filter((t) => !filters.assignedUserId || t.assignedUserId === filters.assignedUserId)
        .filter((t) => !filters.type || t.type === filters.type)
        .filter((t) => !filters.queueId || t.queueId === filters.queueId),
    [filters],
  );

  const filtered = useMemo(() => {
    const byTab = activeTab === 'all' ? items : items.filter((t) => taskMatchesTab(t, activeTab));
    return applyFacets(byTab);
  }, [items, activeTab, applyFacets]);

  // Utenti del sito per il nome dell'assegnatario: la colonna della tabella (FILO-74) e l'avatar
  // delle card. getGroupUsers è cached in userApi (una sola GET per gruppo); cambiando vista si
  // ricostruisce solo la Map in memoria.
  useEffect(() => {
    if (!groupId) return;
    let alive = true;
    userApi
      .getGroupUsers(groupId)
      .then((users) => {
        if (alive) setUsersById(new Map(users.map((u) => [u.id, u.fullName])));
      })
      .catch(() => {
        /* nome e avatar opzionali: senza utenti la colonna mostra «—», silenzioso */
      });
    return () => {
      alive = false;
    };
  }, [groupId]);

  // Code del sito per il filtro (FILO-67: i task di follow-up di una fiera stanno nella coda
  // «{fiera} {anno}»). Tutte e in ordine alfabetico, così gli anni della stessa fiera stanno
  // vicini; l'ordine lo fa il client per non dipendere dai campi ordinabili del REST. Senza code
  // la combo resta con la sola «Tutte le code», silenziosa come i nomi degli utenti.
  const [queues, setQueues] = useState<Queue[]>([]);
  useEffect(() => {
    if (!groupId) return;
    let alive = true;
    queueApi
      .getAll(groupId, { pageSize: FETCH_ALL_PAGE_SIZE })
      .then((page) => {
        if (alive) setQueues([...page.items].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {
        /* filtro opzionale: senza code resta «Tutte le code» */
      });
    return () => {
      alive = false;
    };
  }, [groupId]);

  // Il filtro vive nello slice e sopravvive alla navigazione: se la coda scelta non c'è più
  // (cancellata, o un altro sito) il select mostrerebbe «Tutte le code» con il filtro ancora
  // attivo e la lista vuota. Arrivate le code, un filtro orfano si toglie.
  useEffect(() => {
    if (queues.length > 0 && filters.queueId && !queues.some((q) => q.queueId === filters.queueId))
      dispatch(setTaskFilters({ queueId: undefined }));
  }, [queues, filters.queueId, dispatch]);

  // Vista kanban: gli stessi task (filtrati, ma NON per tab: le colonne sono gli stati),
  // ripartiti per stato visualizzato (taskDisplayStatus).
  const kanbanItems: KanbanBoardItem<Task>[] = useMemo(
    () =>
      applyFacets(items).map((task) => ({
        id: task.taskId,
        columnId: taskDisplayStatus(task),
        data: task,
      })),
    [items, applyFacets],
  );

  const kanbanColumns = useMemo<KanbanColumn[]>(
    () => [
      { id: 'expired', title: t('task.statuses.expired'), tone: 'crit' },
      { id: 'expiring_today', title: t('task.statuses.expiring_today'), tone: 'warn' },
      { id: 'imminent', title: t('task.statuses.imminent'), tone: 'info' },
      { id: 'todo', title: t('task.statuses.todo'), tone: 'todo' },
      { id: 'completed', title: t('task.statuses.completed'), tone: 'ok' },
    ],
    [t],
  );

  /**
   * Il trascinamento di una card: QUI SOLO UN CONFINE È UN VERO CAMBIO DI STATO.
   *
   * «Completato» è l'unico stato persistito: scaduto, in scadenza oggi e imminente li
   * calcola `taskDateBucket` dalla data di scadenza, e il backend scrive solo TODO e
   * COMPLETED. Trascinare un task da «Imminente» a «Scaduto» vorrebbe dire spostargli la
   * scadenza nel passato — che non è un trascinamento, è un calendario.
   *
   * Quindi passano solo i due sensi che significano qualcosa: dentro «Completato» lo
   * completa, fuori da «Completato» lo riapre. Uscendo, la card non finisce per forza nella
   * colonna dove l'hai lasciata: torna `todo` e la sua data decide in quale secchio cade.
   *
   * Gli altri spostamenti si rifiutano CON UN MESSAGGIO. Lasciar tornare indietro la card in
   * silenzio farebbe pensare a un difetto, ed è la domanda che qualcuno farebbe due volte.
   */
  function handleKanbanMove(itemId: string | number, targetColumnId: string | number) {
    const task = items.find((x) => x.taskId === Number(itemId));
    if (!task) return;

    const toCompleted = targetColumnId === 'completed';

    if (toCompleted === isTaskCompleted(task)) {
      toast({ severity: 'info', message: t('task.kanban.moveNotAllowed') });
      return;
    }

    handleToggleCompleted(task);
  }

  function handleCardDelete(task: Task) {
    confirm.requestConfirm(t('task.deleteConfirm', { title: task.title }), () =>
      handleDelete(task.taskId),
    );
  }

  return (
    <div>
      <div className="page-head">
        <div className="page-head__left">
          <div>
            <h1 className="page-title">{t('task.title')}</h1>
            {/* In bacheca il conteggio è quello delle card, che non passano dal filtro
                per tab: le colonne SONO gli stati, e contare i filtrati direbbe un
                numero diverso da quello che si vede. */}
            <span className="page-sub">
              {t('common.record', { count: viewMode === 'kanban' ? kanbanItems.length : filtered.length })}
            </span>
          </div>
          {/* `group` e non `tablist`: questi sono due bottoni a pressione, e lo dicono già con
              `aria-pressed`. Un `tablist` promette il pattern delle schede — figli `role="tab"`
              con `aria-selected`, `aria-controls` verso un `tabpanel`, spostamento con le frecce
              — e un lettore di schermo che sente quella promessa la annuncia a chi poi con le
              frecce non va da nessuna parte. Meglio un ruolo modesto e vero. */}
          <div className="viewtoggle" role="group" aria-label={t('task.view.label')}>
            <button
              type="button"
              className={viewMode === 'table' ? 'on' : undefined}
              aria-pressed={viewMode === 'table'}
              onClick={() => setViewMode('table')}
            >
              <TableGlyph /> {t('task.view.table')}
            </button>
            <button
              type="button"
              className={viewMode === 'kanban' ? 'on' : undefined}
              aria-pressed={viewMode === 'kanban'}
              onClick={() => setViewMode('kanban')}
            >
              <BoardGlyph /> {t('task.view.kanban')}
            </button>
          </div>
        </div>
        <div className="page-actions">
          {/* Vale per tabella e bacheca: tutte e due passano da applyFacets. */}
          <select
            className="filterline"
            value={filters.queueId ?? ''}
            onChange={(e) => dispatch(setTaskFilters({ queueId: e.target.value ? Number(e.target.value) : undefined }))}
            aria-label={t('task.filter.queue')}
          >
            <option value="">{t('task.filter.allQueues')}</option>
            {queues.map((q) => <option key={q.queueId} value={q.queueId}>{q.name}</option>)}
          </select>
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('task.searchPlaceholder')}
              aria-label={t('task.searchPlaceholder')}
            />
          </div>
          <Link to="/settings/queues" className="btn-ghost"><QueuesGlyph /> {t('task.manageQueues')}</Link>
          <ImportButton
            entityLabel={t('task.title').toLowerCase()}
            className="btn-ghost"
            icon={<ImportGlyph />}
            onEntities={async (rows) => {
              for (const raw of rows) await dispatch(createTask(raw as TaskCreatePayload)).unwrap();
            }}
          />
          <button type="button" className="btn-primary" onClick={onCreateClick}>
            <PlusGlyph /> {t('task.createTask')}
          </button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="list-card">
          <div className="list-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={tab.key === activeTab ? 'on' : undefined}
                onClick={() => dispatch(setActiveTab(tab.key))}
              >
                {tab.label} <span className="cnt">{counts[tab.key as keyof typeof counts]}</span>
              </button>
            ))}
          </div>

          {status === 'failed' && <p className="list-message">{t('common.error', { message: error })}</p>}

          {isGloballyEmpty ? (
            <AllTasksDoneCelebration justCompleted={justCompleted} />
          ) : (
            status === 'succeeded' && filtered.length === 0 && (
              <div className="empty-state">
                <p className="text-lg font-semibold">{t('task.emptyTitle')}</p>
                <p>{t('task.emptySubtitle')}</p>
              </div>
            )
          )}

          {filtered.length > 0 && (
            <DataGrid
              rows={filtered}
              columns={taskColumns(t, confirm, handleToggleCompleted, setSelectedTask, handleDelete, usersById)}
              getRowId={(row) => row.taskId}
              rowHeight={64}
              autoHeight
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              loading={status === 'loading'}
              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? '' : 'even-row')}
              className="tk-grid"
            />
          )}
        </div>
      ) : (
        // Bacheca: la stessa `KanbanBoard` della board preventivi. Non una griglia di
        // colonne scritta qui: sarebbe un secondo modo di disegnare la stessa cosa, e le
        // due divergerebbero alla prima modifica. Sta direttamente sulla pagina, senza
        // card di sfondo né tab: le colonne rappresentano già gli stati.
        <KanbanBoard<Task>
          variant="toned"
          columns={kanbanColumns}
          items={kanbanItems}
          emptyLabel={t('task.kanban.emptyColumn')}
          onMove={handleKanbanMove}
          renderCard={(task) => (
            <TaskKanbanCard
              task={task}
              assigneeName={usersById.get(task.assignedUserId)}
              onToggle={() => handleToggleCompleted(task)}
              onOpen={() => setSelectedTask(task)}
              onDelete={() => handleCardDelete(task)}
            />
          )}
        />
      )}

      <ConfirmDialog open={confirm.isOpen} message={confirm.message} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
      {currentSelected && <TaskEditModal task={currentSelected} onClose={() => setSelectedTask(null)} />}
    </div>
  );
}

function taskColumns(
  t: (key: string, opts?: Record<string, unknown>) => string,
  confirm: ReturnType<typeof useConfirmDialog>,
  onToggle: (task: Task) => void,
  onOpen: (task: Task) => void,
  onDelete: (taskId: number) => void,
  usersById: Map<number, string>,
): GridColDef<Task>[] {
  return [
    {
      field: 'completed', headerName: '', sortable: false, filterable: false, hideable: false,
      width: 56, align: 'center', headerAlign: 'center', disableColumnMenu: true,
      renderCell: (p) => {
        const done = p.row.status === 'completed';
        return (
          <span
            className={`tcheck${done ? ' done' : ''}`}
            role="checkbox"
            aria-checked={done}
            tabIndex={0}
            onClick={() => onToggle(p.row)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(p.row); } }}
          >
            <CheckGlyph />
          </span>
        );
      },
    },
    {
      field: 'title', headerName: t('task.columns.title'), flex: 1, minWidth: 220,
      renderCell: (p) => (
        <button type="button" className={`lc-title${p.row.status === 'completed' ? ' done' : ''}`} onClick={() => onOpen(p.row)}>
          {p.value as string}
        </button>
      ),
    },
    {
      field: 'type', headerName: t('task.columns.type'), width: 150,
      valueFormatter: (value: string) => {
        const entry = TASK_TYPES.find((x) => x.value === value);
        return entry ? t(entry.label) : value;
      },
      renderCell: (p) => {
        const entry = TASK_TYPES.find((x) => x.value === p.row.type);
        return <span className="lc-type">{entry ? t(entry.label) : p.row.type}</span>;
      },
    },
    {
      field: 'priority', headerName: t('task.columns.priority'), width: 130,
      renderCell: (p) => {
        const dot = PRIO_DOT[p.row.priority] ?? 'med';
        return <span className="lc-prio"><i className={`pdot ${dot}`} />{t(`task.priorities.${p.row.priority}`)}</span>;
      },
    },
    {
      // FILO-74: il nome e non l'id, così ordinamento e filtro lavorano sul testo che si vede.
      // «—» per un task non assegnato o per un utente che non è più nel sito.
      field: 'assignedUserId', headerName: t('task.columns.assignee'), width: 180,
      valueGetter: (value: number | null | undefined) => (value ? usersById.get(value) ?? '' : ''),
      renderCell: (p) => <span className="lc-assignee">{(p.value as string) || '—'}</span>,
    },
    {
      field: 'expirationDate', headerName: t('task.columns.expiration'), width: 180,
      renderCell: (p) => <span className="lc-when">{p.value ? formatDateTime(p.value as string) : '—'}</span>,
    },
    {
      field: 'status', headerName: t('task.columns.status'), width: 150,
      renderCell: (p) => {
        const done = p.row.status === 'completed';
        const ds = done ? 'completed' : taskDisplayStatus(p.row);
        return <span className={`chip ${STATUS_CHIP[ds] ?? 'neutral'}`}>{t(`task.statuses.${ds}`)}</span>;
      },
    },
    {
      field: 'actions', headerName: t('task.columns.actions'), sortable: false, filterable: false,
      width: 90, align: 'right', headerAlign: 'right',
      renderCell: (p) => (
        <span className="lc-actions">
          <button
            type="button"
            className="lc-act del"
            aria-label={t('common.delete')}
            onClick={() => confirm.requestConfirm(t('task.deleteConfirm', { title: p.row.title }), () => onDelete(p.row.taskId))}
          >
            <TrashGlyph />
          </button>
        </span>
      ),
    },
  ];
}
